import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * Scroll-snap carousel with dots.
 * Markup: <yc-carousel><div ref="track" class="yc-track">…cards…</div><ol ref="dots" class="yc-dots">…</ol></yc-carousel>
 * Dots are buttons (data-index) that scroll to their card; the current dot gets aria-current.
 * When every card is visible (desktop grid), the dots are hidden.
 */
class YcCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    const { track, dots } = this.refs;
    track.addEventListener('scroll', this.#onScroll, { passive: true });
    dots?.addEventListener('click', this.#onDotClick);
    this.#resizeObserver = new ResizeObserver(() => this.#update());
    this.#resizeObserver.observe(track);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track?.removeEventListener('scroll', this.#onScroll);
    this.refs.dots?.removeEventListener('click', this.#onDotClick);
    this.#resizeObserver?.disconnect();
  }

  get cards() {
    return /** @type {HTMLElement[]} */ (Array.from(this.refs.track.children));
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  /** @param {MouseEvent} event */
  #onDotClick = (event) => {
    const dot = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[data-index]') : null);
    if (!dot) return;
    const card = this.cards[Number(dot.dataset.index)];
    if (!card) return;
    const { track } = this.refs;
    const offset = card.offsetLeft - track.offsetLeft - parseFloat(getComputedStyle(track).paddingInlineStart || '0');
    track.scrollTo({ left: offset, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  #update() {
    const { track, dots, progress, counter } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const scrollable = max > 2;
    if (progress instanceof HTMLElement) {
      progress.hidden = !scrollable;
      progress.style.setProperty('--yc-progress', String(scrollable ? Math.min(1, track.scrollLeft / max) : 0));
    }
    if (dots) dots.hidden = !scrollable;
    if (!scrollable) return;

    const cards = this.cards;
    const start = track.getBoundingClientRect().left + parseFloat(getComputedStyle(track).paddingInlineStart || '0');
    let active = 0;
    let best = Infinity;
    cards.forEach((card, index) => {
      const distance = Math.abs(card.getBoundingClientRect().left - start);
      if (distance < best) {
        best = distance;
        active = index;
      }
    });
    if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) active = cards.length - 1;

    if (counter instanceof HTMLElement) {
      counter.textContent = `${active + 1} / ${cards.length}`;
    }
    if (!dots) return;
    dots.querySelectorAll('[data-index]').forEach((dot, index) => {
      dot.setAttribute('aria-current', index === active ? 'true' : 'false');
    });
  }
}

/**
 * "n / N" counter over the product gallery; follows Horizon's slideshow:select event.
 */
class YcMediaCounter extends HTMLElement {
  #gallery = /** @type {Element | null} */ (null);

  connectedCallback() {
    this.#gallery = this.closest('media-gallery') || this.closest('slideshow-component');
    this.#gallery?.addEventListener('slideshow:select', this.#onSelect);
  }

  disconnectedCallback() {
    this.#gallery?.removeEventListener('slideshow:select', this.#onSelect);
  }

  /** @param {Event} event */
  #onSelect = (event) => {
    const detail = /** @type {{ index?: number }} */ (/** @type {CustomEvent} */ (event).detail || {});
    if (typeof detail.index !== 'number') return;
    const current = this.querySelector('[data-current]');
    if (current) current.textContent = String(detail.index + 1);
  };
}

/**
 * UGC video card: swaps the poster for the video (from a <template>) and plays it inline.
 * Starting one video pauses the others.
 */
class YcVideoCard extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#play);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#play);
  }

  /** @param {Event} event */
  #play = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-yc-play]') : null;
    if (!button || this.hasAttribute('playing')) return;
    const template = this.querySelector('template');
    if (!(template instanceof HTMLTemplateElement)) return;

    document.querySelectorAll('yc-video-card[playing] video').forEach((video) => {
      if (video instanceof HTMLVideoElement) video.pause();
    });

    const media = this.querySelector('[data-yc-media]');
    if (!media) return;
    media.append(template.content.cloneNode(true));
    const video = media.querySelector('video');
    this.setAttribute('playing', '');
    if (video) {
      video.setAttribute('playsinline', '');
      video.controls = true;
      video.focus();
      video.play().catch(() => {});
    }
  };
}

/**
 * Native review list: client-side filters and pagination over server-rendered review cards,
 * laid out in masonry columns (2 on mobile, 4 on desktop).
 */
class YcReviews extends Component {
  requiredRefs = ['source', 'columns'];

  #page = 1;
  #filter = 'all';
  /** @type {MediaQueryList} */
  #desktop = matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    this.#desktop.addEventListener('change', this.#render);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.setAttribute('ready', '');
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktop.removeEventListener('change', this.#render);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  get perPage() {
    return Number(this.#desktop.matches ? this.dataset.perPageDesktop : this.dataset.perPageMobile) || 4;
  }

  get columnCount() {
    return this.#desktop.matches ? 4 : 2;
  }

  get items() {
    const all = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-review]')));
    return all.filter((item) => {
      if (this.#filter === 'all') return true;
      if (this.#filter === 'photos') return item.dataset.hasImage === 'true';
      return item.dataset.rating === this.#filter;
    });
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.name !== `yc-review-filter-${this.id}`) return;
    this.#filter = target.value;
    this.#page = 1;
    this.#render();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-page], [data-toggle-filters]') : null;
    if (!(target instanceof HTMLElement)) return;

    if (target.hasAttribute('data-toggle-filters')) {
      const panel = this.refs.filters;
      if (!(panel instanceof HTMLElement)) return;
      const open = panel.hidden;
      panel.hidden = !open;
      this.querySelectorAll('[data-toggle-filters]').forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));
      return;
    }

    const pages = Math.max(1, Math.ceil(this.items.length / this.perPage));
    const value = target.dataset.page;
    if (value === 'prev') this.#page = Math.max(1, this.#page - 1);
    else if (value === 'next') this.#page = Math.min(pages, this.#page + 1);
    else this.#page = Number(value) || 1;
    this.#render();
    this.refs.columns.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  #render = () => {
    const { columns, pagination, range, source } = this.refs;
    // Return every card to the source list first, so none is lost when the columns are rebuilt.
    this.querySelectorAll('[data-review]').forEach((item) => source.append(item));
    const items = this.items;
    const perPage = this.perPage;
    const pages = Math.max(1, Math.ceil(items.length / perPage));
    this.#page = Math.min(this.#page, pages);
    const start = (this.#page - 1) * perPage;
    const visible = items.slice(start, start + perPage);

    const cols = Array.from({ length: this.columnCount }, () => {
      const col = document.createElement('div');
      col.className = 'yc-reviews__column';
      return col;
    });
    visible.forEach((item, index) => cols[index % cols.length]?.append(item));
    columns.replaceChildren(...cols);

    if (pagination instanceof HTMLElement) {
      const buttons = pagination.querySelector('[data-pages]');
      if (buttons) {
        buttons.replaceChildren(
          ...Array.from({ length: pages }, (_, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'yc-reviews__page';
            button.dataset.page = String(index + 1);
            button.textContent = String(index + 1);
            button.setAttribute('aria-label', (this.dataset.pageLabel || 'Page {n}').replace('{n}', String(index + 1)));
            if (index + 1 === this.#page) button.setAttribute('aria-current', 'page');
            return button;
          })
        );
      }
      const prev = pagination.querySelector('[data-page="prev"]');
      const next = pagination.querySelector('[data-page="next"]');
      if (prev instanceof HTMLButtonElement) prev.disabled = this.#page === 1;
      if (next instanceof HTMLButtonElement) next.disabled = this.#page === pages;
      pagination.hidden = items.length === 0;
    }

    if (range instanceof HTMLElement) {
      const template = this.dataset.rangeTemplate || '';
      const total = this.dataset.totalLabel || String(items.length);
      range.textContent = items.length
        ? template
            .replace('{from}', String(start + 1))
            .replace('{to}', String(start + visible.length))
            .replace('{total}', this.#filter === 'all' ? total : String(items.length))
        : this.dataset.emptyLabel || '';
    }
  };
}

/**
 * Quantity breaks: keeps Horizon's add-to-cart form in sync with the selected pack.
 * - Sets the form's variant id and quantity for the main line.
 * - Recalculates prices and placeholder text when variant dropdowns change.
 * - Updates the add-to-cart and sticky-bar labels with the pack price.
 * - Before Horizon adds the main line, adds the pack's other lines (other variants
 *   picked in the dropdowns and free gifts) so the cart drawer shows everything.
 */
class YcQuantityBreaks extends HTMLElement {
  /** @type {Array<{id: number, title: string, price: number, compare: number, available: boolean}>} */
  #variants = [];
  /** @type {HTMLFormElement | null} */
  #form = null;
  #submitting = false;

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-yc-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    const section = this.closest('.shopify-section') || document;
    this.#form = /** @type {HTMLFormElement | null} */ (
      section.querySelector('product-form-component form[data-type="add-to-cart-form"]')
    );
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onCardClick);
    window.addEventListener('submit', this.#onSubmit, { capture: true });
    this.#update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onCardClick);
    window.removeEventListener('submit', this.#onSubmit, { capture: true });
  }

  get #tier() {
    const input = this.querySelector('.yc-qb-tier__input:checked');
    return /** @type {HTMLElement | null} */ (input?.closest('[data-yc-tier]') ?? null);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target instanceof HTMLElement && event.target.closest('[data-yc-tier]')) this.#update();
  };

  /** Clicking anywhere on a card (gift rows included) selects it; dropdowns keep their own clicks. */
  #onCardClick = (/** @type {MouseEvent} */ event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('select, label, a, button')) return;
    const tier = target.closest('[data-yc-tier]');
    const input = tier?.querySelector('.yc-qb-tier__input');
    if (input instanceof HTMLInputElement && !input.disabled && !input.checked) {
      input.checked = true;
      this.#update();
    }
  };

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(Math.round(cents), this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'INR');
  }

  /** Variant ids for each piece of a tier (from its dropdowns, or the default variant). */
  #slots(tier) {
    const quantity = Number(tier.dataset.quantity) || 1;
    const selects = Array.from(tier.querySelectorAll('select[data-yc-slot]'));
    if (selects.length) return selects.map((select) => Number(/** @type {HTMLSelectElement} */ (select).value));
    const fallback = Number(this.#form?.querySelector('input[name="id"]')?.getAttribute('value')) ||
      this.#variants.find((v) => v.available)?.id || this.#variants[0]?.id;
    return Array.from({ length: quantity }, () => fallback);
  }

  #pricesFor(tier) {
    const slots = this.#slots(tier);
    const quantity = slots.length;
    let subtotal = 0;
    let compare = 0;
    for (const id of slots) {
      const variant = this.#variants.find((v) => v.id === id);
      if (!variant) continue;
      subtotal += variant.price;
      compare += Math.max(variant.compare || 0, variant.price);
    }
    const value = Number(tier.dataset.discountValue) || 0;
    let discount = 0;
    if (tier.dataset.discountType === 'percentage') discount = Math.round((subtotal * value) / 100);
    else if (tier.dataset.discountType === 'amount_each') discount = Math.round(value * 100 * quantity);
    else if (tier.dataset.discountType === 'amount_total') discount = Math.round(value * 100);
    discount = Math.min(discount, subtotal);
    const total = subtotal - discount;
    const saving = Math.max(0, compare - total);
    return {
      quantity,
      total,
      compare,
      saving,
      percent: compare > 0 ? Math.floor((saving * 100) / compare) : 0,
      unit: quantity ? Math.floor(total / quantity) : total,
    };
  }

  /** @param {string} template @param {ReturnType<YcQuantityBreaks['#pricesFor']>} p */
  #fill(template, p) {
    return template
      .replaceAll('[quantity]', String(p.quantity))
      .replaceAll('[price]', this.#money(p.total))
      .replaceAll('[compare_price]', this.#money(p.compare))
      .replaceAll('[save_amount]', this.#money(p.saving))
      .replaceAll('[save_percent]', String(p.percent))
      .replaceAll('[unit_price]', this.#money(p.unit));
  }

  #update() {
    let selected = null;
    for (const tier of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-yc-tier]'))) {
      const p = this.#pricesFor(tier);
      const price = tier.querySelector('[data-yc-price]');
      if (price) price.textContent = this.#money(p.total);
      const compare = tier.querySelector('[data-yc-compare]');
      const compareValue = tier.querySelector('[data-yc-compare-value]');
      if (compare instanceof HTMLElement) compare.hidden = p.compare <= p.total;
      if (compareValue) compareValue.textContent = this.#money(p.compare);
      tier.querySelectorAll('[data-yc-tpl]').forEach((el) => {
        el.textContent = this.#fill(el.getAttribute('data-yc-tpl') || '', p);
        if (el instanceof HTMLElement && el.hasAttribute('data-yc-needs-saving')) el.hidden = p.saving <= 0;
      });
      if (tier.querySelector('.yc-qb-tier__input:checked')) selected = { tier, p };
    }
    if (!selected) return;

    this.#syncForm(selected.tier);
    const label = this.#fill(this.dataset.buttonLabel || '', selected.p);
    const section = this.closest('.shopify-section') || document;
    const mainButton = this.#form?.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
    if (label && mainButton && !mainButton.hasAttribute('disabled')) {
      const textNode = Array.from(mainButton.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim());
      if (textNode) textNode.textContent = label;
    }
    const stickyPrice = section.querySelector('.sticky-add-to-cart__price') || document.querySelector('.sticky-add-to-cart__price');
    if (stickyPrice) stickyPrice.textContent = this.#money(selected.p.total);
  }

  /** Lines for the selected tier: [{ id, quantity, gift }] grouped by variant. */
  #lines(tier) {
    /** @type {Map<number, number>} */
    const counts = new Map();
    for (const id of this.#slots(tier)) if (id) counts.set(id, (counts.get(id) || 0) + 1);
    const lines = Array.from(counts, ([id, quantity]) => ({ id, quantity, gift: false }));
    tier.querySelectorAll('[data-yc-gift]').forEach((gift) => {
      const id = Number(gift.getAttribute('data-yc-gift'));
      if (id) lines.push({ id, quantity: 1, gift: true });
    });
    return lines;
  }

  #syncForm(tier) {
    const form = this.#form;
    if (!form) return;
    const [main] = this.#lines(tier);
    if (!main) return;
    const idInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    if (idInput instanceof HTMLInputElement) idInput.value = String(main.id);
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = String(main.quantity);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    if (event.target !== this.#form) return;
    const tier = this.#tier;
    if (!tier) return;
    this.#syncForm(tier);
    if (this.#submitting) {
      this.#submitting = false;
      return;
    }
    const extras = this.#lines(tier).slice(1);
    if (!extras.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const giftNote = this.dataset.giftProperty || '';
    try {
      await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: extras.map((line) => ({
            id: line.id,
            quantity: line.quantity,
            ...(line.gift && giftNote ? { properties: { [giftNote]: '✓' } } : {}),
          })),
        }),
      });
    } catch (error) {
      console.error(error);
    }
    // Let Horizon add the main line; its response re-renders the cart with every line.
    this.#submitting = true;
    const submitter = event.submitter instanceof HTMLElement ? event.submitter : undefined;
    this.#form?.requestSubmit(submitter);
  };
}

if (!customElements.get('yc-carousel')) customElements.define('yc-carousel', YcCarousel);
if (!customElements.get('yc-media-counter')) customElements.define('yc-media-counter', YcMediaCounter);
if (!customElements.get('yc-video-card')) customElements.define('yc-video-card', YcVideoCard);
if (!customElements.get('yc-reviews')) customElements.define('yc-reviews', YcReviews);
if (!customElements.get('yc-quantity-breaks')) customElements.define('yc-quantity-breaks', YcQuantityBreaks);
